import React, { FC, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Modal, Typography, Grid } from '@mui/material'
import { ContractAuthorizedPersonsQuery } from '../../../graphql'
import { useAlert, useIsLimitedContract } from '../../../hooks'

import { InviteFormGeneral } from '../forms/InviteFormGeneral'
import {
  ButtonWithTooltip,
  CloseModalButton,
  isModalDialogOpen as isModalDialogOpenVar,
  isModalPromptOpen,
} from '../../../components'
import { useTeamTableUpdate } from '../hooks'
import { StyledWrap, ModalContent, ModalBody } from './StyledComponents'

export const InviteTeammate: FC<{
  personsData: ContractAuthorizedPersonsQuery | undefined
  refetch: () => void
}> = ({ personsData, refetch }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isDirty, setIsDirty] = useState<boolean>(false)

  const { resetAlertData } = useAlert()

  useTeamTableUpdate(personsData, refetch, isOpen)

  const { t } = useTranslation()

  const { isLimitedContract } = useIsLimitedContract()

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    resetAlertData()
  }, [])

  const handleClose = useCallback(() => {
    if (isDirty) {
      isModalPromptOpen(false)
      isModalDialogOpenVar(true)
      return
    }
    setIsOpen(false)
  }, [isDirty])

  return (
    <StyledWrap>
      <ButtonWithTooltip
        disabledOperationName="Inviting teammates"
        disabled={isLimitedContract}
        onClick={handleOpen}
        variant="contained"
        disableFocusRipple
        data-test="inviteTeammate"
        fullWidth={true}
        color="inherit"
        sx={{
          whiteSpace: 'nowrap',
        }}
      >
        {t('inviteTeammate', 'Invite teammate')}
      </ButtonWithTooltip>

      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="account-modal-title"
        aria-describedby="account-modal-description"
        sx={{
          '& [tabindex]': {
            outline: 'none',
          },
        }}
      >
        <ModalContent container data-test="inviteTeammateModal">
          <Grid
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            p={3}
            pl={2}
          >
            <Typography variant={'h5'}>{t('inviteAteammate', 'Invite a teammate')}</Typography>
            <CloseModalButton onClick={handleClose} data-test="closeModal" />
          </Grid>
          <ModalBody size={12}>
            <InviteFormGeneral setIsOpen={setIsOpen} setIsDirty={setIsDirty} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </StyledWrap>
  )
}

export default InviteTeammate
